import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import cn from 'classnames';
import { getSearchParam } from 'helpers/getSearchParam';
import PropTypes from 'prop-types';

import {
  getPromotionCategoryList,
  getPromotionsList,
  isEmptyCollection,
  isEmptyObject,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { Icon, Label, NavigationMenu } from '@atlas/ui';

import { defaultRoutes } from 'components/MainRouter/constants';

import PromotionCard from './PromotionCard';
import PromotionListSkeleton from './PromotionListSkeleton';

const ALL_PROMO_ID = 'all_promo';
const ALL_PROMO_TAB = { id: ALL_PROMO_ID, name: 'All promo' };

const usePromotionsData = (searchCategory, isInitialLoadWithQueryParam) => {
  const { authenticated } = useApplicationState();
  const { lang } = useSettings();
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const { push: navigate } = useHistory();
  const promotionsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.PROMOTIONS);

  useEffect(() => {
    getPromotionCategoryList()
      .then(data => {
        if (isEmptyObject(data)) throw new Error('Promotion not found');

        const parsedCategories = data
          .map(category => ({
            id: String(category.id),
            name: category?.title || category.name, // title property is the one correct with translation
            orderPosition: category?.order_position,
          }))
          .sort((a, b) => {
            if (a.orderPosition === b.orderPosition) {
              return Number(a.id) - Number(b.id); // sorting case because there is no validation for the uniqueness of the orderPosition
            }
            return a.orderPosition - b.orderPosition;
          });
        setCategories(parsedCategories);
      })
      .catch(() => {
        setCategories([]);
      });
  }, [lang]);

  useEffect(() => {
    if (searchCategory && isEmptyCollection(categories)) {
      return;
    }

    const userVisibilities = ['public', authenticated ? 'registered' : 'anonymous'];
    const isExistingCategory = categories.map(category => category.id).includes(searchCategory);
    const categoryId = isExistingCategory ? parseInt(searchCategory) : undefined;

    setLoading(true);
    getPromotionsList(userVisibilities, categoryId)
      .then(promos => {
        // Redirect only on initial load with query param, existing category, and empty promotions
        if (
          isEmptyCollection(promos) &&
          isExistingCategory &&
          isInitialLoadWithQueryParam &&
          !hasLoadedOnceRef.current
        ) {
          navigate(promotionsPath);
        }
        setPromotions(promos);
        hasLoadedOnceRef.current = true;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    lang,
    authenticated,
    navigate,
    promotionsPath,
    searchCategory,
    categories,
    isInitialLoadWithQueryParam,
  ]);

  const sortedPromotions = useMemo(
    () => promotions?.sort((a, b) => a.order_position - b.order_position),
    [promotions],
  );

  return { categories, loading, promotions: sortedPromotions };
};

const CategoryMenu = ({ activeCategory, categories, onChange }) => (
  <div className="promotion-categories-menu-wrapper">
    <Label
      className={cn('navigation-menu__all-promo', activeCategory === ALL_PROMO_TAB.id && 'active')}
      message="all_promo_category"
      onClick={() => onChange(ALL_PROMO_TAB.id)}
    />
    <NavigationMenu
      className="promotion-categories-menu"
      items={categories}
      onClick={onChange}
      selectedItem={activeCategory}
    />
  </div>
);

CategoryMenu.propTypes = {
  activeCategory: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

const PromotionList = () => {
  const promotionsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.PROMOTIONS);
  const { menu } = defaultRoutes;
  const { push: navigate } = useHistory();

  const searchCategory = getSearchParam(window.location.search, 'category') || undefined;
  const isInitialLoadWithQueryParamRef = useRef(!!searchCategory);

  const { categories, loading, promotions } = usePromotionsData(
    searchCategory,
    isInitialLoadWithQueryParamRef.current,
  );

  const activeCategory = useMemo(() => {
    if (!searchCategory || isEmptyCollection(categories)) {
      return ALL_PROMO_ID;
    }
    const isExistingCategory = categories.map(category => category.id).includes(searchCategory);
    return isExistingCategory ? searchCategory : ALL_PROMO_ID;
  }, [searchCategory, categories]);

  const handleCategoryChange = useCallback(
    value => {
      isInitialLoadWithQueryParamRef.current = false;
      navigate(value !== ALL_PROMO_ID ? `${promotionsPath}?category=${value}` : promotionsPath);
    },
    [promotionsPath, navigate],
  );

  return (
    <div className="promotion_page">
      <Link className="back-to-container" to={menu}>
        <Icon className="icon-back" name="back" />
        <h1>
          <Label className="title-text" message="Promotion" />
        </h1>
      </Link>

      <div className="promotion-list-inner">
        {!isEmptyCollection(categories) && (
          <CategoryMenu
            activeCategory={activeCategory}
            categories={categories}
            onChange={handleCategoryChange}
          />
        )}

        <PromotionListSkeleton loading={loading}>
          <div className="promotion-list">
            {isEmptyCollection(promotions) ? (
              <div className="promotion-list-empty">
                <Label className="promotion-list-empty-title" message="promo_empty_title" />
                <Label className="promotion-list-empty-subtitle" message="promo_empty_text" />
              </div>
            ) : (
              promotions?.map(promo => (
                <PromotionCard
                  actionUrl={promo.action_url}
                  buttonText={promo.button}
                  description={promo.description}
                  id={promo.id}
                  imageUrl={promo.image_url}
                  key={promo.id}
                  layout={promo.layout}
                  link={promo.link}
                  shortContent={promo.short_content}
                />
              ))
            )}
          </div>
        </PromotionListSkeleton>
      </div>
    </div>
  );
};

export default PromotionList;
